import { exists } from '../helpers';
import { Component, ComponentStyle, SiteStyle } from './schema';

/** Normalizes the difference between component and site styles */
interface StyleTypeclass<T> {
  name(v: Readonly<T>): string;
  type(v: Readonly<T>): ComponentStyle['type'];
  comb(v: Readonly<T>): ComponentStyle['comb'];
  children(v: Readonly<T>): readonly string[];
  id(v: Readonly<T>): string;
  replaceChildren(v: Readonly<T>, children: string[]): T;
}
export const componentStyleTypeclass: StyleTypeclass<ComponentStyle> = {
  name: (v) => v.name,
  type: (v) => v.type,
  comb: (v) => v.comb,
  children: (v) => v.children,
  id: (v) => v._id,
  replaceChildren: (v, children) => ({ ...v, children }),
};
export const siteStyleTypeclass: StyleTypeclass<SiteStyle> = {
  name: (v) => v.data.name,
  type: (v) => v.data.type,
  comb: (v) => v.data.comb,
  children: (v) => v.data.children,
  id: (v) => v._id,
  replaceChildren: (v, children) => ({ ...v, data: { ...v.data, children } }),
};

/**
 * Adds styles from the "newStyles" list into the "existingStyles" list.
 * Styles from the "existingStyles" win over "newStyles" if the have the
 * same name.
 **/
export function mergeStyles<T>(
  existingStylesFlat: readonly T[],
  newStylesFlat: readonly T[],
  $: StyleTypeclass<T>,
): {
  styles: T[];
  idUpdates: Map<string, string>;
} {
  const existingStyles = new Map(existingStylesFlat.map((s) => [$.id(s), s]));
  const newStyles = new Map(newStylesFlat.map((s) => [$.id(s), s]));
  const idUpdates = new Map<string, string>();

  function add(newStyle: T, existingSameLevel: readonly T[]): string {
    // Just use the existing style, if one exists with the same name.
    const mergeWith =
      existingSameLevel.find(
        (s) => $.name(s) === $.name(newStyle) && $.type(s) === $.type(newStyle),
      ) ?? newStyle;
    if ($.id(newStyle) !== $.id(mergeWith)) {
      idUpdates.set($.id(newStyle), $.id(mergeWith));
    }
    // If the new style has new children (compared to `mergeWith`), add those:
    const existingChildren = $.children(mergeWith)
      .map((c) => outputStyles.get(c))
      .filter(exists);
    const children = new Set($.children(mergeWith));
    for (const newChildId of $.children(newStyle)) {
      const newChild = newStyles.get(newChildId);
      if (!newChild) continue;
      children.add(add(newChild, existingChildren));
    }
    outputStyles.set(
      $.id(mergeWith),
      $.replaceChildren(mergeWith, [...children]),
    );
    return $.id(mergeWith);
  }

  const outputStyles = new Map(existingStyles);
  const existingRoots = [...existingStyles.values()].filter(
    (s) => $.comb(s) === '',
  );

  // This should be all the new styles where $.comb(newStyle) === ''.
  // HOWEVER some of our components have root-ish styles which have
  // a comb set but their parent isn't present.  So we define "root"
  // as not having a parent instead.  (slightly more broad).
  const newRootIds = new Set(newStylesFlat.map($.id));
  for (const newStyle of newStylesFlat) {
    for (const childId of $.children(newStyle)) {
      newRootIds.delete(childId);
    }
  }
  for (const id of newRootIds) {
    add(newStyles.get(id)!, existingRoots);
  }
  return {
    styles: [...outputStyles.values()],
    idUpdates,
  };
}

/**
 * LOSSILY converts a SiteStyle to a ComponentStyle.
 * You should NOT save the output of this function back into webflow.
 */
export function approxSiteStyleToComponent({
  _id,
  data,
}: SiteStyle): ComponentStyle {
  return {
    _id,
    fake: false,
    type: data.type,
    name: data.name,
    namespace: '',
    comb: data.comb,
    styleLess: data.styleLess,
    // remove the sel field from variants - if this is present the style duplicates
    variants: Object.fromEntries(
      Object.entries(data.variants).map(([k, v]) => [
        k,
        { ...v, sel: undefined },
      ]),
    ),
    children: data.children,
  };
}

/**
 * Given a list of combo classnames that should apply to a style,
 * get the list of style ids that should be applied.
 */
export function resolveComboClass(
  names: readonly string[],
  stylesById: ReadonlyMap<string, ComponentStyle>,
): string[] {
  const [rootName, ...combNames] = names;

  const root = [...stylesById.values()].find(
    (x) => x.comb === '' && x.name === rootName && x.type === 'class',
  );
  if (root == null) throw new Error(`no root class found for ${rootName}`);
  const styles = [root];

  for (const name of combNames) {
    const parent = styles[styles.length - 1];
    const match = parent.children
      .map((c) => stylesById.get(c))
      .find((x) => x && x.type === 'class' && x.name === name);
    if (match == null) {
      throw new Error(
        `no match for combo class ${name} in ${parent.name}/${parent._id}`,
      );
    }
    styles.push(match);
  }

  return styles.map((x) => x._id);
}

export function applyClassIdUpdates(
  maping: ReadonlyMap<string, string>,
  component: Component,
): Component {
  function getUpdatedId(id: string): string {
    const updated = maping.get(id);
    return updated ? getUpdatedId(updated) : id;
  }

  const nodes = component.payload.nodes.map((n) => {
    if (n.text) return n;
    return {
      ...n,
      classes: n.classes.map(getUpdatedId),
    };
  });
  return { ...component, payload: { ...component.payload, nodes } };
}
